import {
  ChatUIEnhancementCategoryCard,
  ChatUIEnhancementContentCard,
  MappedMessageType,
} from 'Assistant/__generated__/client.schemas';
import type { generateResponseConversationPostResponse200 } from 'Assistant/__generated__/client';
import { ChatbotMessageRole } from './enums';

// A single line on the /conversation stream: a success chunk or the terminal error frame.
export type ConversationStreamingFrame =
  generateResponseConversationPostResponse200['data'];

type CommonMessageDetails = {
  id: string;
  createdAt: string;
  type: MappedMessageType;
};

export type UserMessage = CommonMessageDetails & {
  content: string;
  topic: string | null;
  starterId: string | null;
};

export type AssistantMessage = CommonMessageDetails & {
  source: AssistantSourceChunk[];
  role: ChatbotMessageRole.assistant;
  messageEnd?: boolean;
};

type ChatbotTextMessage = {
  value: string;
  type: string;
};

export interface ChatbotMetadata {
  topic?: string | null;
  starterId?: string | null;
}

export interface ChatbotMessageInput {
  id?: string | null;
  body: string;
  role: ChatbotMessageRole;
  metadata?: ChatbotMetadata | null;
}

export interface SubmitChatbotMessagesInput {
  messages: ChatbotMessageInput[];
  conversationId?: string | null;
  clientSentAtUtcTimestamp?: string | null;
}

export type AssistantStreamMessage = {
  id: string;
  content: [ChatbotTextMessage];
  messageEnd?: boolean;
};

export type AssistantSourceChunk =
  | { kind: 'text'; value: string }
  | { kind: 'content_card'; value: ChatUIEnhancementContentCard }
  | { kind: 'category_card'; value: ChatUIEnhancementCategoryCard };

export type Message =
  | (UserMessage & { role: ChatbotMessageRole.user })
  | (CommonMessageDetails & {
      source: AssistantSourceChunk[];
      role: ChatbotMessageRole.assistant;
    });

export type ConversationUpdateType = 'NEW_MESSAGE' | 'LOAD_MORE';

export type HistoryErrorType =
  | 'RETRY'
  | 'FEATURE_UNAVAILABLE'
  | 'USER_SESSION_EXPIRED'
  | null;

export type ScrollLocation = 'BOTTOM' | 'CURRENT';

// Genuine UI entry points for the `ai-coach-chatbot-message-sent` event.
// `conversation-chat-input` is shared with the mobile app (keep it in sync);
// the `home-*` values are web-specific entry points. This is the single source
// of truth — the runtime guard (`isChatbotMessageSentSource`) derives its
// allowed values from this array.
export const CHATBOT_MESSAGE_SENT_SOURCES = [
  'conversation-chat-input',
  'home-chat-input',
  'home-conversation-starter',
] as const;

// `'unknown'` is not a real entry point and is intentionally excluded from the
// array above (so it isn't accepted as untrusted input). It's assigned as a
// fallback when an initial message arrives without a valid source.
export type ChatbotMessageSentSource =
  | (typeof CHATBOT_MESSAGE_SENT_SOURCES)[number]
  | 'unknown';

export type ConversationSession = {
  date: Date;
  messages: Message[];
};

export type OnSubmit = (message: UserMessage) => Promise<void>;
